// MARK://dhk8/pos/lib/print/printHtml
// Browser-side print primitive. Renders HTML into a hidden iframe and triggers the
// native print dialog via the iframe's contentWindow.print(). The OS handles the
// thermal printer (USB/Bluetooth/network) as the user's chosen destination.
//
// This is the "modo A" path of the printing flex model: the cloud-direct deployment
// (no DocketPad / no edge brain) still gets tickets out by leveraging the device's
// connected printer. The browser remembers the chosen destination once the user picks
// it in the print dialog; subsequent prints from the same device go straight through.

export interface PrintHtmlOptions {
  /** The full HTML document (or fragment — we wrap it). Inline <style> recommended. */
  html: string
  /** Sets <title> — shown in the dialog and used as default filename if saved. */
  title?: string
  /** Default 'thermal-80mm'. Sets the @page size hint. */
  paperKind?: 'thermal-58mm' | 'thermal-80mm' | 'A4'
  /** If true, removes iframe after print dialog dismisses. Default true. */
  cleanup?: boolean
}

const PAGE_CSS: Record<NonNullable<PrintHtmlOptions['paperKind']>, string> = {
  'thermal-58mm': '@page { size: 58mm auto; margin: 2mm; }',
  'thermal-80mm': '@page { size: 80mm auto; margin: 2mm; }',
  'A4':           '@page { size: A4; margin: 12mm; }',
}

/**
 * Opens a hidden iframe with the given HTML, waits for it to settle, then fires
 * window.print() inside the iframe. Returns a promise that resolves when the user
 * dismisses the print dialog (cancel or print).
 *
 * Note: there's no reliable way to know if printing succeeded — the browser doesn't
 * expose that. We assume success on dialog dismissal. For audit, the calling code
 * should also POST to /v1/print/jobs as a record (status QUEUED is fine).
 */
export function printHtml(opts: PrintHtmlOptions): Promise<void> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    document.body.appendChild(iframe)

    const paperKind = opts.paperKind ?? 'thermal-80mm'
    const title = opts.title ?? 'Ticket'
    const cleanup = opts.cleanup ?? true

    // Build complete HTML. The @page rule MUST live in CSS inside the iframe.
    const fullHtml = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${PAGE_CSS[paperKind]}
  body { margin: 0; font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace; font-size: 11px; line-height: 1.35; color: #000; }
  * { box-sizing: border-box; }
</style>
</head>
<body>${opts.html}</body>
</html>`

    const doc = iframe.contentDocument || iframe.contentWindow?.document
    if (!doc) { resolve(); return }
    doc.open()
    doc.write(fullHtml)
    doc.close()

    const fire = () => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
      } catch {
        // ignore — best effort
      }
      // afterprint fires once dialog dismissed in modern browsers.
      const onAfter = () => {
        iframe.contentWindow?.removeEventListener('afterprint', onAfter)
        if (cleanup) {
          // small delay so the print render isn't yanked while the engine reads it
          setTimeout(() => { try { iframe.remove() } catch { /* noop */ } }, 250)
        }
        resolve()
      }
      iframe.contentWindow?.addEventListener('afterprint', onAfter)
      // safety net: resolve after 30s no matter what
      setTimeout(() => {
        if (cleanup) { try { iframe.remove() } catch { /* noop */ } }
        resolve()
      }, 30_000)
    }

    // Give the iframe a tick to render fonts + CSS before printing.
    if (doc.readyState === 'complete') {
      setTimeout(fire, 60)
    } else {
      iframe.onload = () => setTimeout(fire, 60)
    }
  })
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string)
}
